/*
 * THE CONFIRM FOR EVERY CHANGE STATUS… OPTION, AND WHAT EACH ONE WRITES.
 *
 * record/endings.mjs decides what the menu offers and holds the server's reason
 * lists; its confirmFor covers the endings (Remove, Lost, Disqualify, Vendor, Not a
 * lead, Churned, Won, Hold). The brief asks more of the menu than that: every
 * option has "a confirm that says what it changes, and an optional note to the
 * timeline" (item 11), so nothing on the position pill changes the company from a
 * single misplaced click. This module is the rest of that, kept beside the
 * endings rather than inside them so the menu's builder owns it:
 *
 *   Move to {stage}   "Move Northwind back to Pricing sent?" with the note, which
 *                     endings' writesFor drops (a stage move is one PATCH there)
 *   Not now           "Set Northwind aside until tomorrow?" with the skip's own note
 *   Mark as vendor    the vendorEffects sentence, plus the timeline note
 *   Hold until…       the sentence says the DAY, worked out the way the server
 *                     resolves the preset (src/db/followUps.js resolveSnoozeUntil),
 *                     instead of "until in 3 days"
 *
 * Every write is still an existing endpoint (build map [22]); the only addition is
 * the note, which goes on the timeline as a note activity because status_reason has
 * no room for prose.
 *
 *   confirmSpec(id, ctx)            → { title, fields, sentence(choice), ... } | null
 *   confirmWrites(id, choice, ctx)  → [{ method, path, body }]
 *   readyToConfirm(spec, choice)    → true when every required field is filled
 *
 * Pure, so test/record-status-confirm.test.js pins the words without a browser.
 */
import { confirmFor, writesFor } from "./endings.mjs";
import { STAGE_LABELS } from "./situation.mjs";
import { plural, toDate, shortDate } from "./format.mjs";

// Where a confirm's note goes, said under the field so he knows who will read it.
export const NOTE_LABELS = {
  timeline: "Note to the timeline · optional",
  // A hold's note and Not now's are the snooze's own (follow_up_state.snooze_note):
  // they come back on the Next line "Snoozed until Mon · …", not on the timeline.
  hold: "Note · optional, shown with the hold",
  // Not a lead deletes the timeline; the note stays on the ignore receipt.
  receipt: "Note · optional, kept with the receipt under Ignored",
};

const OPEN_STAGES = ["new", "call_scheduled", "in_progress", "proposal_sent", "evaluating", "agreement"];

// What leaving each out-of-pipeline status takes with it, so a move from one says
// so (updateCompanyPipeline clears recycle_until and re_engage_after on the way out).
const LEAVES = {
  recycled: "The park ends and its wake-up date is cleared.",
  lost: "Closed lost ends and its re-engage date is cleared.",
  not_a_fit: "It stops being disqualified.",
  vendor: "It stops being a vendor.",
  advisory: "It stops being advisory.",
  churned: "It becomes a sales conversation again; the churn stays in its history.",
};

/** "YYYY-MM-DD" in the browser's own calendar, the shape date inputs and PATCH take. */
export function ymd(ts) {
  const d = toDate(ts);
  if (!d) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A calendar date ("2026-10-05") read as midday local, so no zone moves its day. */
export function dayOf(value) {
  if (!value) return null;
  return toDate(`${String(value).slice(0, 10)}T12:00:00`);
}

/** `now` plus whole calendar days or months (setDate / setMonth, as the server does). */
export function addCalendar(now, { days = 0, months = 0 } = {}) {
  const d = new Date(toDate(now).getTime());
  if (months) d.setMonth(d.getMonth() + months);
  if (days) d.setDate(d.getDate() + days);
  return d;
}

/*
 * The day a hold preset lands on, the server's way (resolveSnoozeUntil): tomorrow,
 * three days, the next Monday strictly after today, two weeks. After their next
 * reply has no day of its own.
 */
export function holdUntil(preset, now = new Date()) {
  switch (preset) {
    case "tomorrow":
      return addCalendar(now, { days: 1 });
    case "3_days":
      return addCalendar(now, { days: 3 });
    case "next_monday": {
      const d = toDate(now);
      const ahead = (8 - d.getDay()) % 7 || 7;
      return addCalendar(now, { days: ahead });
    }
    case "2_weeks":
      return addCalendar(now, { days: 14 });
    default:
      return null;
  }
}

/** "until Mon, Oct 5", "until tomorrow morning", "until their next reply". */
export function holdWords(choice = {}, now = new Date()) {
  if (choice.preset === "until_reply") return "until their next reply";
  if (choice.preset === "tomorrow") return "until tomorrow morning";
  const d = choice.preset === "date" ? dayOf(choice.date) : holdUntil(choice.preset, now);
  if (!d) return "until the day you pick";
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  return `until ${weekday}, ${shortDate(d, { now })}`;
}

/** Which lost reasons are not earned yet, in one line: "Declined after pricing: no pricing on record yet". */
export function unearnedWords(reasons) {
  const off = (reasons || []).filter((r) => r && r.enabled === false);
  if (!off.length) return null;
  // Grouped by why, so two reasons missing the same evidence read as one sentence.
  const byWhy = new Map();
  for (const r of off) {
    const why = r.why || "not earned yet";
    if (!byWhy.has(why)) byWhy.set(why, []);
    byWhy.get(why).push(r.label);
  }
  return [...byWhy.entries()]
    .map(([why, labels]) => `${labels.join(" and ")}: ${why}`)
    .join(". ");
}

const ACTIVE = (a) => a && a.kind && a.kind !== "meeting_today" && a.kind !== "meeting_soon";

/**
 * The confirm for any option the menu offers. Returns
 *   { title, fields, sentence(choice), confirmLabel, noteLabel, reasons?, remind?,
 *     presets?, defaults?, unearned?, dateLimits? }
 * or null for an id nothing knows.
 *
 * `fields` lists what the card asks for, in the order it draws them: 'reason',
 * 'remind', 'preset', 'date', 'notice', 'reason_text', 'note'.
 */
export function confirmSpec(id, { company = null, contact = null, act = null, now = new Date() } = {}) {
  const name = (company && company.name) || "This company";
  const today = ymd(now);
  const tomorrow = ymd(addCalendar(now, { days: 1 }));

  if (String(id).startsWith("move:")) {
    const stage = String(id).slice(5);
    const label = STAGE_LABELS[stage] || stage;
    const live = company && company.status === "prospect" && OPEN_STAGES.includes(company.pipeline_stage);
    const back = live && OPEN_STAGES.indexOf(stage) < OPEN_STAGES.indexOf(company.pipeline_stage);
    const from = live ? STAGE_LABELS[company.pipeline_stage] : null;
    return {
      title: live ? `Move ${name} ${back ? "back " : ""}to ${label}?` : `Put ${name} back on the board at ${label}?`,
      fields: ["note"],
      noteLabel: NOTE_LABELS.timeline,
      confirmLabel: "Confirm",
      defaults: {},
      sentence: () => {
        const move = live
          ? `${name} moves from ${from} ${back ? "back " : ""}to ${label}.`
          : `${name} comes back on the board at ${label}. ${LEAVES[company && company.status] || ""}`.trim();
        return `${move} Next follows the new stage, and the timeline shows the move as set by hand.`;
      },
    };
  }

  if (id === "not_now") {
    const lines = ((act && act.actions) || []).filter(ACTIVE).length;
    // The lines stay on the record: a skip takes the company off Today and
    // Follow-ups, and getCompanyActions still lists what is owed.
    const stay = lines === 1 ? " The line in Next stays here." : lines > 1 ? ` The ${plural(lines, "line")} in Next stay here.` : "";
    return {
      title: `Set ${name} aside until tomorrow?`,
      fields: ["note"],
      noteLabel: NOTE_LABELS.hold,
      confirmLabel: "Confirm",
      defaults: {},
      sentence: () => `${name} keeps its stage and leaves your queues until tomorrow morning.${stay} Nothing is sent.`,
    };
  }

  const base = confirmFor(id, { company, contact, act, now });
  if (!base) return null;
  const spec = { ...base, defaults: base.defaults || {}, noteLabel: NOTE_LABELS.timeline };

  switch (id) {
    case "vendor":
      // The vendor confirm says what happens (vendorEffects) and, like every
      // option, takes a note for the timeline.
      spec.fields = [...base.fields, "note"];
      break;
    case "not_a_lead":
      spec.noteLabel = NOTE_LABELS.receipt;
      break;
    case "hold":
      spec.noteLabel = NOTE_LABELS.hold;
      spec.dateLimits = { min: tomorrow };
      spec.sentence = (c = {}) => `${name} keeps its stage and leaves your queues ${holdWords(c, now)}.`;
      break;
    case "remove":
      // A reminder date is a day ahead; the server takes today as "wake now".
      spec.dateLimits = { min: tomorrow };
      spec.sentence = base.sentence;
      break;
    case "disqualify":
      spec.sentence = base.sentence;
      break;
    case "won":
      // Won on a day that has happened.
      spec.dateLimits = { max: today };
      break;
    case "churn":
      spec.dateLimits = { max: today };
      break;
    case "lost":
      spec.unearned = unearnedWords(base.reasons);
      spec.sentence = base.sentence;
      break;
    default:
      break;
  }
  return spec;
}

/**
 * The confirm a menu item opens. Every option opens one (the brief: "a confirm
 * that says what it changes"), including a stage move and Not now, which
 * changeStatusOptions marks as writing straight away; the menu itself never writes.
 */
export function confirmIdFor(item) {
  if (!item || item.disabled) return null;
  if (String(item.id).startsWith("move:")) return item.id;
  if (item.id === "not_now") return "not_now";
  return item.confirm || item.id;
}

/** Every field the confirm needs before its Confirm can be pressed. */
export function readyToConfirm(spec, choice = {}) {
  if (!spec) return false;
  const has = (f) => spec.fields.includes(f);
  if (has("reason")) {
    if (!choice.reason) return false;
    const r = (spec.reasons || []).find((x) => x.id === choice.reason);
    if (r && r.enabled === false) return false;
  }
  if (has("remind") && choice.remind === "date" && !choice.date) return false;
  if (has("preset") && choice.preset === "date" && !choice.date) return false;
  if (has("date") && !has("preset") && !choice.date) return false;
  if (has("notice") && !choice.notice) return false;
  return true;
}

function noteActivity(note, { company, contact }, title) {
  const text = String(note || "").trim();
  if (!text || !company) return [];
  return [
    {
      method: "POST",
      path: "/activities",
      body: { type: "note", company_id: company.id, contact_id: contact ? contact.id : null, title, body: text },
    },
  ];
}

/**
 * The writes for one confirmed option, in order: the status change first, the
 * note after it, so a note never lands on a company whose change was refused.
 */
export function confirmWrites(id, choice = {}, { company = null, contact = null, now = new Date() } = {}) {
  const ctx = { company, contact, now };
  if (String(id).startsWith("move:")) {
    const stage = String(id).slice(5);
    return [...writesFor(id, choice, ctx), ...noteActivity(choice.note, ctx, `Moved to ${STAGE_LABELS[stage] || stage}`)];
  }
  if (id === "not_now") {
    const note = String(choice.note || "").trim();
    return [{ method: "POST", path: `/follow-ups/${company && company.id}/skip`, body: note ? { note } : {} }];
  }
  if (id === "vendor") return [...writesFor(id, choice, ctx), ...noteActivity(choice.note, ctx, "Marked as vendor")];
  return writesFor(id, choice, ctx);
}

